import { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { useImmer } from 'use-immer'
import { Header, Scrollable, Pagination, useIsFormSubmitting } from '@/components/DashboardComponents'
import { Button, ButtonLabel, ButtonHint, Textbox, Spacing, RadioGroup, RadioOption, Checkbox, HalfSpacing } from '@/form/FormComponents'
import { Icon } from '@/components/Icon'
import { FormProvider, FormObjectGroup, FormDisabled, FormDataProvider, FormSubmitProvider, useFormSubmit, useLeavePageModal } from '@/form/FormProvider';
import { Modal, ModalContent, ModalButtons, useModal } from '@/components/Modal';
import { FilePicker } from '@/form/FilePicker';
import { markdownToHTML } from '@/shared/markdown';
import { useBlocker } from '@/systems/Blocker';
import { EscapeForm } from '@/form/FormProvider';
import { publicAction } from '@/data/loaders';
import Lottie from "lottie-react";
import sparklesAnimation from '@/../assets/icons8-sparkles.json';
import { useResizeEffect, useIsTouch } from '@/hooks/useIsMobile';
import { NavigateBackOnSuccess } from '@/pages/NewScenarioLayout';

export function RubricEditor({ goBackCallback, rubric, from_scenario_editor, from_assessment = false, submitCallback, actionData, navigate }) {

  const is_touch = useIsTouch();
  const scroller_ref = useRef(null);

  const submitting = useIsFormSubmitting();

  const [initial_data, setInitialData] = useState({
    title: rubric?.data?.title || null,
    grading_instructions: rubric?.data?.grading_instructions || null
  });

  const [step, setStep] = useState(rubric ? "rubric_editor" : "welcome"); // welcome, generating, rubric_editor

  const [scoring, setScoring] = useState(rubric?.data?.scoring != null ? rubric.data.scoring : true);
  const [levels, updateLevels] = useImmer(rubric?.data?.levels || [{ title: "Completed", points: 1, description: null }]);
  const [rows, updateRows] = useImmer(convertSectionsToRows(rubric?.data?.sections || []));

  const [openChooseLevelsModal] = useModal(({ closeModal }) => {
    function submitForm({ formatted_data }) {
      setStep("rubric_editor");
      closeModal();

      const level = formatted_data.levels == "other" ? Math.floor(formatted_data.other_levels) : Number(formatted_data.levels);

      const checklist_level_templates = {
        "2": ["Not Done", "Done"],
        "3": ["Not Done", "Partially Done", "Done"],
      }

      const performance_level_templates = {
        "1": ["Completed"],
        "2": ["Not Done", "Done"],
        "3": ["Does Not Meet Expectations", "Meets Expectations", "Exceeds Expectations"],
        "4": ["Below", "Approaching", "Meets", "Exceeds"],
        "5": ["Novice", "Advanced Beginner", "Competent", "Proficient", "Expert"],
        "6": ["Novice", "Advanced Beginner", "Competent", "Proficient", "Expert", "Master"]
      }

      const templates = (formatted_data.levels == "2" || formatted_data.levels == "3") ? checklist_level_templates : performance_level_templates;

      const levels = templates[level] || Array.from({length: level}, (_, i) => `Level ${i + 1}`);

      const start_at_0 = formatted_data.levels == "2" || formatted_data.levels == "3" || formatted_data.start_at_0;

      updateLevels(() => {
        return levels.map((level, index) => ({ title: level, points: start_at_0 ? index : index + 1, description: null }));
      });
    }

    return (
      <Modal title="Choose a template">
        <FormProvider onSubmit={submitForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <RadioGroup name="levels" required="Please choose an option">
                    <RadioOption name="2" title="Checklist" text="Not Done / Done"/>
                    <RadioOption name="3" text="Not Done / Partially Done / Done"/>
                    <Spacing/>
                    <RadioOption name="4" title="Performance levels" text="4 levels"/>
                    <RadioOption name="5" text="5 levels"/>
                    <RadioOption name="6" text="6 levels"/>
                    <RadioOption name="other" text="Other:"/>
                    <FormDataProvider fields={["levels"]}>
                      {({data}) => {
                        return (
                          <>
                            <FormDisabled obviated={data.levels != "other"} indented={true}>
                              <div style={{width: "150px"}}>
                                <Textbox name="other_levels" type="number" min={1} autofocus={data.levels == "other"} required={data.levels == "other"}/>
                              </div>
                            </FormDisabled>
                            <FormDisabled obviated={data.levels == "2" || data.levels == "3"}>
                              <Checkbox name="start_at_0" text="Start scoring at 0"/>
                            </FormDisabled>
                          </>
                        )
                      }}
                    </FormDataProvider>
                  </RadioGroup>
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Continue" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  const [openAddOrEditLevelModal] = useModal(({ context, closeModal }) => {
    function submitForm({ formatted_data, focusError }) {

      if (context.index !== undefined) {
        updateLevels(draft => {
          draft[context.index] = formatted_data;
        });
      } else {
        updateLevels(draft => {
          draft.splice(context.new_index, 0, formatted_data);
        });
      }

      closeModal();
      
    }

    const initial_data = context.index !== undefined ? levels[context.index] : undefined;

    return (
      <Modal title={context.index !== undefined ? "Edit level" : "Add level"}>
        <FormProvider onSubmit={submitForm} initial_data={initial_data}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <div className="rubric_level_top_boxes">
                    <div>
                      <Textbox name="title" title="Level title" placeholder="Beginner" required="Please enter a level title"/>
                    </div>
                    {scoring ? <div>
                      <Textbox name="points" title="Points" placeholder="1" required="Missing" type="number" autocomplete="none" min={0}/>
                    </div> : null}
                  </div>
                  <Spacing/>
                  <Textbox name="description" title="Description" markdown={true} markdown_minimal={true} className="short"/>
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  const [removeLevelModal] = useModal(function ({ context, closeModal }) {
    function submitRemoveLevel() {
      updateLevels(draft => {
        draft.splice(context.index, 1);
      });

      closeModal();
    }

    return (
      <Modal title="Remove this level?">
        <ModalContent>
          {"You can't undo this."}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Remove level" className="destructive" onClick={submitRemoveLevel}/>
        </ModalButtons>
      </Modal>
    )
  });

  const [scrollToBottom, setScrollToBottom] = useState(false);

  const [openAddOrEditRowModal] = useModal(({ context, closeModal }) => {
    function submitForm({ formatted_data, focusError }) {

      if (context.is_section) {
        formatted_data.is_section = true;
      }

      if (formatted_data.descriptors) {
        formatted_data.descriptors = Object.values(formatted_data.descriptors);
      }

      if (context.index !== undefined) {
        updateRows(draft => {
          draft[context.index] = formatted_data;
        });
      } else {
        updateRows(draft => {
          draft.splice(context.new_index, 0, formatted_data);
        });

        if (context.new_index == rows.length) {
          setScrollToBottom(true);
        }
      }

      closeModal();
      
    }

    const initial_data = context.index !== undefined ? rows[context.index] : undefined;

    if (context.is_section) {
      return (
        <Modal title={context.index !== undefined ? "Edit section" : "Add section"}>
          <FormProvider onSubmit={submitForm} initial_data={initial_data}>
            <FormSubmitProvider>
              {({handleSubmit}) => (
                <>
                  <ModalContent>
                    <Textbox name="title" title="Section title" required="Please enter a section title"/>
                    <Spacing/>
                    <Textbox name="description" title="Description" markdown={true} markdown_minimal={true} className="short"/>
                  </ModalContent>
                  <ModalButtons>
                    <Button text="Cancel" className="hollow" onClick={closeModal}/>
                    <Button text="Submit" className="primary" onClick={handleSubmit}/>
                  </ModalButtons>
                </>
              )}
            </FormSubmitProvider>
          </FormProvider>
        </Modal>
      )
    } else {
      return (
        <Modal title={context.index !== undefined ? "Edit row" : "Add row"} full_screen={true}>
          <FormProvider onSubmit={submitForm} initial_data={initial_data}>
            <FormSubmitProvider>
              {({handleSubmit}) => (
                <>
                  <ModalContent>
                    <div className="rubric_row_top_boxes">
                      <div>
                        <Textbox name="title" title="Criterion title" autofocus={context.autofocus_level_index ? undefined : "always"} required="Please enter a criterion title"/>
                      </div>
                      <div>
                        <Textbox name="description" title="Criterion description" markdown={true} markdown_minimal={true} className="short"/>
                      </div>
                    </div>
                    <Spacing/>
                    <HalfSpacing/>
                    <FormObjectGroup name="descriptors">
                      <div className="rubric_row_bottom_boxes">
                        {levels.map((level, index) => {
                          return (
                            <div key={index}>
                              <Textbox name={String(index)} title={level.title+(scoring ? ` • ${level.points || 0} pt${(level.points || 0) > 1 ? "s" : ""}` : "")} markdown={true} markdown_minimal={true} className="short" autofocus={index == context.autofocus_level_index ? "always" : undefined} />
                            </div>
                          )
                        })}
                      </div>
                    </FormObjectGroup>
                  </ModalContent>
                  <ModalButtons>
                    <Button text="Cancel" className="hollow" onClick={closeModal}/>
                    <Button text="Save changes" className="primary" onClick={handleSubmit}/>
                  </ModalButtons>
                </>
              )}
            </FormSubmitProvider>
          </FormProvider>
        </Modal>
      )
    }
  })

  const [removeRowModal] = useModal(function ({ context, closeModal }) {
    function submitRemoveRow() {
      updateRows(draft => {
        draft.splice(context.index, 1);
      });

      closeModal();
    }

    return (
      <Modal title="Remove this row?">
        <ModalContent>
          {"You can't undo this."}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Remove row" className="destructive" onClick={submitRemoveRow}/>
        </ModalButtons>
      </Modal>
    )
  });

  useEffect(function() {
    if (scrollToBottom) {
      let ref = scroller_ref.current.querySelector(".dashboard_content_inner");
      ref.scrollTop = ref.scrollHeight;
      setScrollToBottom(false);
    }
  }, [scrollToBottom, scroller_ref]);

  const [is_uploading, setIsUploading] = useState(false);

  const handleUploadStart = () => {
    setIsUploading(true);
  }

  const [openImportErrorModal] = useModal(function ({ closeModal }) {
    return (
      <Modal title="Something went wrong">
        <ModalContent>
          {"There was a problem importing your rubric. Please make sure the file is readable and formatted like a checklist or table."}
        </ModalContent>
        <ModalButtons>
          <Button text="Dismiss" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  });

  const handleUploadComplete = (value) => {
    setIsUploading(false);
    if (step == "welcome") {
      setStep("generating");

      publicAction("convertRubric", { file_id: value._id }).then(function({ data }) {

        const rubric = data.data;
        
        setStep("rubric_editor");

        setInitialData({
          title: rubric.data.title,
          grading_instructions: rubric.data.grading_instructions
        });

        setScoring(rubric.data.scoring);

        updateLevels(() => {
          return rubric.data.levels;
        });

        updateRows(() => {
          return convertSectionsToRows(rubric.data.sections);
        });

      }).catch(function(error) {
        console.error(error);
        openImportErrorModal();
        setStep("welcome");
      });
    }
  }

  const handleUploadError = () => {
    setIsUploading(false);
  }

  const [openShouldSaveModal] = useModal(({ context, closeModal, setLoading }) => {
    function submitForm({ formatted_data, focusError }) {

      if (formatted_data.reuse == "yes") {
        setLoading(true);

        publicAction("createOrUpdateRubric", {
          data: {
            title: formatted_data.title,
            grading_instructions: formatted_data.grading_instructions,
            scoring: scoring,
            levels: levels,
            sections: convertRowsToSections(rows),
          }
        }).then(({ data }) => {

          closeModal();
          context.callback({ title: formatted_data.title, _id: data.id, version_id: data.version_id });

        }).catch((error) => {
          console.error(error);
          setLoading(false);
          focusError({name: "title", error: "Something went wrong. Please try again."})
        })
      } else {
        closeModal();
        context.callback({ title: null });
      }

    }

    return (
      <Modal title="Create rubric">
        <FormProvider onSubmit={submitForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <RadioGroup title={"Save this rubric so you can reuse it?"} name="reuse" required="Please choose an option">
                    <RadioOption name="yes" text="Yes, save to my rubrics"/>
                    <RadioOption name="no" text={"No, use just for this "+(from_assessment ? "assessment" : "scenario")}/>
                  </RadioGroup>
                  <FormDataProvider fields={["reuse"]}>
                    {({data}) => {
                      return (
                        <div style={{display: data.reuse !== "yes" ? "none" : "contents"}}>
                          <Spacing/>
                          <Textbox name="title" title="Rubric title" default_value={context.title} autofocus={data.reuse == "yes"} required={data.reuse == "yes" ? "Please enter a rubric title" : false}/>
                        </div>
                      )
                    }}
                  </FormDataProvider>
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Continue" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  const [openUpdateOriginalRubricModal] = useModal(({ context, closeModal, setLoading }) => {
    function submitForm({ formatted_data, focusError }) {

      if (formatted_data.update == "yes") {
        setLoading(true);

        publicAction("createOrUpdateRubric", {
          id: context.id,
          data: {
            title: context.title,
            scoring: scoring,
            levels: levels,
            sections: convertRowsToSections(rows),
            grading_instructions: context.grading_instructions
          }
        }).then(({ data }) => {

          closeModal();
          context.callback({ updated: true, version_id: data.version_id });

        }).catch((error) => {
          if (error.status === 404) {
            focusError({name: "update", error: "You either don't have permission to update the original rubric file, or it was deleted. You can update just for this "+(from_assessment ? "assessment" : "scenario")+" instead."})
          } else {
            console.error(error);
            focusError({name: "update", error: "Something went wrong. Please try again."})
          }
          setLoading(false);
        })
      } else {
        closeModal();
        context.callback({ updated: false });
      }

    }

    return (
      <Modal title="Update the original rubric file?">
        <FormProvider onSubmit={submitForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <RadioGroup name="update" required="Please choose an option" default_value="yes">
                    <RadioOption name="yes" text="Yes, update the original rubric file"/>
                    <RadioOption name="no" text={"No, update just for this "+(from_assessment ? "assessment" : "scenario")}/>
                  </RadioGroup>
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Continue" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  function handleSubmitRubricForm({ formatted_data }) {
    function doSubmitRubric({ title, _id, version_id }) {
      submitCallback({ rubric: {
        ...(_id ? { _id, version_id } : { local_id: rubric?.local_id || String(Date.now()) }),
        data: {
          title,
          scoring: scoring,
          levels: levels,
          sections: convertRowsToSections(rows),
          grading_instructions: formatted_data.grading_instructions
        }
      } });
    }

    if (from_scenario_editor) {
      if (rubric) {
        if (rubric._id && rubric.is_linked) {
          if (["edit", "owner"].includes(rubric.file?._level)) {
            openUpdateOriginalRubricModal({ id: rubric._id, title: formatted_data.title, grading_instructions: formatted_data.grading_instructions, callback: ({updated, version_id}) => {
              if (updated) {
                doSubmitRubric({ title: formatted_data.title, _id: rubric._id, version_id });
              } else {
                doSubmitRubric({ title: formatted_data.title });
              }
            } });
          } else {
            doSubmitRubric({ title: formatted_data.title });
          }
        } else {
          doSubmitRubric({ title: rubric.data.title || null });
        }
      } else {
        openShouldSaveModal({ title: initial_data?.title || null, callback: doSubmitRubric });
      }
    } else {
      doSubmitRubric({ title: formatted_data.title });
    }
  }

  return (
    <>
      <div className="dashboard_main full_height" ref={scroller_ref}>
        <Scrollable className="dashboard_content no_header" depencencies={[step]}>
          <Header title={rubric ? "Edit rubric" : "New rubric"} backButton={true}/>
          {(step == "welcome" || step == "generating") ? (
            <>
              <EscapeForm>
                {from_scenario_editor ? <RubricEditorBlocker goBackCallback={goBackCallback} step="welcome"/> : null}
                <div className="dashboard_content_inner nearest_scrollable_container">
                  {step == "generating" ? (
                    <div className="error_box">
                      <div>
                        <div className="sparkles_animation"><Lottie animationData={sparklesAnimation} /></div>
                        <div className="text_ai_shimmer">Creating your rubric...</div>
                      </div>
                    </div>
                  ) : null}
                  {step == "welcome" ? (
                    <div className="dashboard_content_container">
                      <div className="small_width_section">
                        <ButtonLabel title="New rubric..."/>
                        <div className="buttons">
                          <Button icon="square-edit-outline" text="Start from scratch" disabled={is_uploading} onClick={() => {openChooseLevelsModal()}}/>
                        </div>
                        <HalfSpacing/>
                        <FormProvider>
                          <FilePicker name="rubric_files" type="rubric" gallery={false} onUploadStart={handleUploadStart} onUploadComplete={handleUploadComplete} onUploadError={handleUploadError}>
                            {({is_uploading, disabled, obviated, handleChooseFile}) => {
                              return <>
                                <div className="buttons">
                                  <Button icon="upload" text="Upload a PDF" onClick={handleChooseFile} disabled={disabled || is_uploading} obviated={obviated} loading={is_uploading}/>
                                </div>
                                <ButtonHint hint={`For Google Docs or Word, save as a PDF first.`}/>
                              </>
                            }}
                          </FilePicker>
                        </FormProvider>
                      </div>
                    </div>
                  ) : null}
                </div>
              </EscapeForm>
            </>
          ) : (
            <FormProvider key="rubric_editor" onSubmit={handleSubmitRubricForm} initial_data={initial_data} has_changed={rubric ? (JSON.stringify(rubric.data.levels) !== JSON.stringify(levels) || JSON.stringify(rubric.data.sections) !== JSON.stringify(convertRowsToSections(rows)) || rubric.data.scoring !== scoring) : (rows.length > 0)} unsaved_modal={!from_scenario_editor ? (actionData?.success ? null : "new_rubric") : null}>
              {from_scenario_editor ? <RubricEditorBlocker goBackCallback={goBackCallback} step="rubric_editor"/> : null}
              {!from_scenario_editor && <NavigateBackOnSuccess actionData={actionData} navigate={navigate} />}
              <div className="dashboard_content_inner nearest_scrollable_container">
                <div className="dashboard_content_container">
                  {is_touch && (
                    <div className="callout_hint_box">
                      <div>
                        <div><Icon name="cursor-default-outline"/></div>
                        <div>{`You need a mouse to edit rubrics. It doesn't work with touch screens yet.`}</div>
                      </div>
                    </div>
                  )}
                  {(rubric?._id || !from_scenario_editor) ? <>
                    <Textbox name="title" title="Rubric title" autofocus={true} required="Please enter a rubric title"/>
                    <Spacing/>
                  </> : null}
                  <HalfSpacing/>
                  <div className="rubric_editor_scrollable">
                    <RubricEditorTable rows={rows} levels={levels} scoring={scoring} setScoring={setScoring} openAddOrEditLevelModal={openAddOrEditLevelModal} removeLevelModal={removeLevelModal} openAddOrEditRowModal={openAddOrEditRowModal} removeRowModal={removeRowModal} scroller_ref={scroller_ref} />
                  </div>
                  {!is_touch && <>
                    <Spacing/>
                    <div className="buttons" style={{paddingLeft: "14px"}}>
                      <Button text="Add row" className="primary" backgroundColor="var(--header)" icon="plus" onClick={(event) => {event.stopPropagation(); openAddOrEditRowModal({new_index: rows.length})}}/>
                      <Button text="Add section" className="hollow" backgroundColor="var(--header)" icon="plus" onClick={(event) => {event.stopPropagation(); openAddOrEditRowModal({new_index: rows.length, is_section: true})}}/>
                    </div>
                  </>}
                  <div style={{display: is_touch ? "none" : ""}}>
                    <Spacing/>
                    <HalfSpacing/>
                    <Textbox name="grading_instructions" title="Extra instructions for the AI grader" multiline={true} height="long"/>
                  </div>
                </div>
              </div>
              <Pagination>
                <FormSubmitProvider>
                  {({handleSubmit, has_changed}) => (
                    <Button className="primary" text={rubric ? "Save changes" : "Create rubric"} onClick={handleSubmit} disabled={rows.length == 0 || submitting || !has_changed || actionData?.success}/>
                  )}
                </FormSubmitProvider>
              </Pagination>
            </FormProvider>
          )}
        </Scrollable>
      </div>
    </>
  )
}

export function RubricEditorTable({ rows, levels, scoring, setScoring, openAddOrEditLevelModal, removeLevelModal, openAddOrEditRowModal, removeRowModal, scroller_ref, readOnly = false }) {
  const is_touch = useIsTouch();
  const [rubric_row_criterion_width, setRubricRowCriterionWidth] = useState(100);

  const rows_have_content = rows.some(row => !row.is_section && row.descriptors.some(level => level)) || rows.length == 0;

  const handleLayoutOrResize = function() {
    if (document.body.querySelector(".rubric_editor_table")) {
      setRubricRowCriterionWidth(document.body.querySelector(".rubric_editor_table").querySelector(".rubric_editor_row:not(.rubric_editor_row_section)").children[0].clientWidth);
    }
  }

  useLayoutEffect(function() {
    handleLayoutOrResize();
  }, [setRubricRowCriterionWidth, levels, rows]);

  useResizeEffect(function() {
    handleLayoutOrResize();
  });

  const [headerStuckStuck, setHeaderStuckStuck] = useState(false);
  const [headerStuckVisible, setHeaderStuckVisible] = useState(false);
  const headerStuckHeightRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(function() {
    if (headerStuckHeightRef.current) {
      const resizeObserver = new ResizeObserver(function(entries) {
        for (let entry of entries) {
          setHeaderHeight(entry.contentRect.height);
        }
      });
      
      resizeObserver.observe(headerStuckHeightRef.current);
      
      return function() {
        resizeObserver.disconnect();
      };
    }
  }, [levels, scoring]);

  useEffect(function() {
    let ref = scroller_ref?.current?.querySelector(".dashboard_content_inner");
    if (ref) {
      const handleScroll = function() {
        const headerEl = ref.querySelector(".rubric_editor_header:not(.rubric_editor_header_stuck)");
        const stuckHeaderEl = ref.querySelector(".rubric_editor_header_stuck");
        if (!headerEl || !stuckHeaderEl) return;
        
        const containerRect = ref.getBoundingClientRect();
        const headerRect = headerEl.getBoundingClientRect();
        const stuckHeaderRect = stuckHeaderEl.getBoundingClientRect();
        
        let stuck_visible = headerRect.top < containerRect.top;
        setHeaderStuckVisible(stuck_visible);

        let stuck_stuck = stuck_visible && stuckHeaderRect.top < containerRect.top + 20;
        setHeaderStuckStuck(stuck_stuck);
      }
      ref.addEventListener("scroll", handleScroll);
      return function() {
        ref.removeEventListener("scroll", handleScroll);
      }
    }
  }, [scroller_ref, headerStuckVisible, levels]);

  return (
    <div className={"rubric_editor_table "+(readOnly ? "read_only" : "")} style={{gridTemplateColumns: "minmax(0, 1.85fr) "+levels.map(() => `minmax(0, ${!rows_have_content ? "0.5" : "1"}fr)`).join(" "), "--rubric_row_criterion_width": rubric_row_criterion_width+"px"}} inert={is_touch ? "" : undefined}>
      <div className={"rubric_editor_header"} inert={headerStuckVisible ? "" : undefined} style={{visibility: headerStuckVisible ? "hidden" : "visible"}}>
        <div>
          {!readOnly && <Checkbox value={scoring} text="Use scoring" default_value={true} onChange={(value) => {setScoring(value)}} disabled={is_touch}/>}
        </div>
        {levels.map((level, index) => {
          return (
            <div key={index} className="rubric_editor_header_level" tabIndex={0} onClick={() => {openAddOrEditLevelModal?.({index})}}>
              <div key={index}>
                <div className="rubric_editor_level_title"><div>{level.title}</div>{scoring ? <div>{`${(level.points || 0)} pt${(level.points || 0) > 1 ? "s" : ""}`}</div> : null}</div>
                {level.description ? <div className="rubric_editor_level_description markdown collapse_padding" dangerouslySetInnerHTML={{__html: markdownToHTML(level.description)}}></div> : null}
                {!readOnly && <>
                  <div className="rubric_editor_row_actions left">
                    <Button className="small primary" backgroundColor="var(--header)" icon="plus" onClick={(event) => {event.stopPropagation(); openAddOrEditLevelModal?.({new_index: index})}}/>
                  </div>
                  <div className="rubric_editor_row_actions top">
                    {levels.length > 1 ? <Button className="small hollow destructive" icon="close" onClick={(event) => {event.stopPropagation(); removeLevelModal?.({index})}}/> : null}
                  </div>
                  <div className="rubric_editor_row_actions right">
                    <Button className="small primary" backgroundColor="var(--header)" icon="plus" onClick={(event) => {event.stopPropagation(); openAddOrEditLevelModal?.({new_index: index+1})}}/>
                  </div>
                </>}
              </div>
            </div>
          )
        })}
      </div>
      <div className={"rubric_editor_header rubric_editor_header_stuck "+(headerStuckStuck ? "stuck" : "")} style={{marginTop: "-"+headerHeight+"px", visibility: headerStuckVisible ? "visible" : "hidden", maxHeight: (headerHeight == 0 ? 0 : "")}} inert={headerStuckVisible ? undefined : ""}>
        <div ref={headerStuckHeightRef}></div>
        {levels.map((level, index) => {
          return (
            <div key={index} className="rubric_editor_header_level" tabIndex={0} onClick={() => {openAddOrEditLevelModal?.({index})}}>
              <div key={index}>
                <div className="rubric_editor_level_title"><div>{level.title}</div>{scoring ? <div>{`${(level.points || 0)} pt${(level.points || 0) > 1 ? "s" : ""}`}</div> : null}</div>
                {level.description ? <div className="rubric_editor_level_description markdown collapse_padding" dangerouslySetInnerHTML={{__html: markdownToHTML(level.description)}}></div> : null}
                {!readOnly && <>
                  <div className="rubric_editor_row_actions left">
                    <Button className="small primary" backgroundColor="var(--header)" icon="plus" onClick={(event) => {event.stopPropagation(); openAddOrEditLevelModal?.({new_index: index})}}/>
                  </div>
                  {headerStuckStuck ? null : <div className="rubric_editor_row_actions top">
                    {levels.length > 1 ? <Button className="small hollow destructive" icon="close" onClick={(event) => {event.stopPropagation(); removeLevelModal?.({index})}}/> : null}
                  </div>}
                  <div className="rubric_editor_row_actions right">
                    <Button className="small primary" backgroundColor="var(--header)" icon="plus" onClick={(event) => {event.stopPropagation(); openAddOrEditLevelModal?.({new_index: index+1})}}/>
                  </div>
                </>}
              </div>
            </div>
          )
        })}
      </div>
      {rows.map((row, index) => {
        if (row.is_section) {
          return (
            <div className={"rubric_editor_row rubric_editor_row_section "+(!rows_have_content ? "rows_have_no_content" : "")} key={index} tabIndex={0} onClick={() => {openAddOrEditRowModal?.({index, is_section: true})}}>
              <div>
                <div className="rubric_editor_row_title">{row.title}</div>
                {row.description ? <div className="rubric_editor_row_description markdown collapse_padding" dangerouslySetInnerHTML={{__html: markdownToHTML(row.description)}}></div> : null}
                {!readOnly && <>
                  <div className="rubric_editor_row_actions top">
                    <div style={{display: "flex", flexDirection: "row", cursor: "default"}}>
                      <Button text="Row" className="small primary" backgroundColor="var(--header)" icon="plus" onClick={(event) => {event.stopPropagation(); openAddOrEditRowModal?.({new_index: index})}}/>
                      <Button text="Section" className="small hollow" backgroundColor="var(--header)" icon="plus" onClick={(event) => {event.stopPropagation(); openAddOrEditRowModal?.({new_index: index, is_section: true})}}/>
                    </div>
                  </div>
                  <div className="rubric_editor_row_actions left delete_left">
                    <Button className="small hollow destructive" icon="close" onClick={(event) => {event.stopPropagation(); removeRowModal?.({index})}}/>
                  </div>
                  <div className="rubric_editor_row_actions bottom">
                    <div style={{display: "flex", flexDirection: "row", cursor: "default"}}>
                      <Button text="Row" className="small primary" backgroundColor="var(--header)" icon="plus" onClick={(event) => {event.stopPropagation(); openAddOrEditRowModal?.({new_index: index+1})}}/>
                      <Button text="Section" className="small hollow" backgroundColor="var(--header)" icon="plus" onClick={(event) => {event.stopPropagation(); openAddOrEditRowModal?.({new_index: index+1, is_section: true})}}/>
                    </div>
                  </div>
                </>}
              </div>
            </div>
          )
        }

        return (
          <div className="rubric_editor_row" key={index} tabIndex={0} onClick={() => {openAddOrEditRowModal?.({index})}}>
            <div>
              <div className="rubric_editor_row_title">{row.title}</div>
              {row.description ? <div className="rubric_editor_row_description markdown collapse_padding" dangerouslySetInnerHTML={{__html: markdownToHTML(row.description)}}></div> : null}
              {!readOnly && <>
                <div className="rubric_editor_row_actions top">
                  <div style={{display: "flex", flexDirection: "row", cursor: "default"}}>
                    <Button text="Row" className="small primary" backgroundColor="var(--header)" icon="plus" onClick={(event) => {event.stopPropagation(); openAddOrEditRowModal?.({new_index: index})}}/>
                    <Button text="Section" className="small hollow" backgroundColor="var(--header)" icon="plus" onClick={(event) => {event.stopPropagation(); openAddOrEditRowModal?.({new_index: index, is_section: true})}}/>
                  </div>
                </div>
                <div className="rubric_editor_row_actions left delete_left">
                  <Button className="small hollow destructive" icon="close" onClick={(event) => {event.stopPropagation(); removeRowModal?.({index})}}/>
                </div>
                <div className="rubric_editor_row_actions bottom">
                  <div style={{display: "flex", flexDirection: "row", cursor: "default"}}>
                    <Button text="Row" className="small primary" backgroundColor="var(--header)" icon="plus" onClick={(event) => {event.stopPropagation(); openAddOrEditRowModal?.({new_index: index+1})}}/>
                    <Button text="Section" className="small hollow" backgroundColor="var(--header)" icon="plus" onClick={(event) => {event.stopPropagation(); openAddOrEditRowModal?.({new_index: index+1, is_section: true})}}/>
                  </div>
                </div>
              </>}
            </div>
            {levels.map((level, level_index) => {
              return (
                <div className="rubric_editor_row_level" key={level_index} onClick={(event) => {event.stopPropagation(); openAddOrEditRowModal?.({index, autofocus_level_index: level_index})}}>
                  <div className="markdown collapse_padding" dangerouslySetInnerHTML={{__html: markdownToHTML(row.descriptors[level_index] || "")}}></div>
                </div>
              )
            })}
          </div>
        )
      })}
      {rows.length == 0 ? (
        <div className="rubric_editor_row rubric_editor_row_placeholder">
          <div></div>
          {levels.map((level, index) => {
            return (
              <div className="rubric_editor_row_level" key={index}></div>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

export function RubricEditorTableThumbnail({ rows, levels, scoring }) {
  const rows_have_content = rows.some(row => !row.is_section && row.descriptors.some(level => level)) || rows.length == 0;
  
  return (
    <div className={"rubric_editor_table read_only"} style={{gridTemplateColumns: "minmax(0, 1.85fr) "+levels.map(() => `minmax(0, ${!rows_have_content ? "0.5" : "1"}fr)`).join(" ")}} inert="true">
      <div className={"rubric_editor_header"} style={{display: "none"}}></div>
      <div className={"rubric_editor_header"}>
        <div></div>
        {levels.map((level, index) => {
          return (
            <div key={index} className="rubric_editor_header_level">
              <div key={index}>
                <div className="rubric_editor_level_title"><div>{level.title}</div>{scoring ? <div>{`${(level.points || 0)} pt${(level.points || 0) > 1 ? "s" : ""}`}</div> : null}</div>
                {level.description ? <div className="rubric_editor_level_description markdown collapse_padding" dangerouslySetInnerHTML={{__html: markdownToHTML(level.description)}}></div> : null}
              </div>
            </div>
          )
        })}
      </div>
      {rows.slice(0, 15).map((row, index) => {
        if (row.is_section) {
          return (
            <div className={"rubric_editor_row rubric_editor_row_section "+(!rows_have_content ? "rows_have_no_content" : "")} key={index}>
              <div>
                <div className="rubric_editor_row_title">{row.title}</div>
                {row.description ? <div className="rubric_editor_row_description markdown collapse_padding" dangerouslySetInnerHTML={{__html: markdownToHTML(row.description)}}></div> : null}
              </div>
            </div>
          )
        }

        return (
          <div className="rubric_editor_row" key={index}>
            <div>
              <div className="rubric_editor_row_title">{row.title}</div>
              {row.description ? <div className="rubric_editor_row_description markdown collapse_padding" dangerouslySetInnerHTML={{__html: markdownToHTML(row.description)}}></div> : null}
            </div>
            {levels.map((level, level_index) => {
              return (
                <div className="rubric_editor_row_level" key={level_index}>
                  <div className="markdown collapse_padding" dangerouslySetInnerHTML={{__html: markdownToHTML(row.descriptors[level_index] || "")}}></div>
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

function RubricEditorBlocker({ goBackCallback, step }) {
  const [openBlockerModal] = useLeavePageModal();
  const { has_changed } = useFormSubmit();

  useBlocker({ id: "rubric_editor", block_by_default: true, beforeunload: false, type: "navigate", priority: 4, callback: useCallback(({ proceed }) => {
    if (has_changed && step !== "welcome") {
      openBlockerModal({ leavePage: goBackCallback });
      return true;
    } else {
      goBackCallback();
      return false;
    }
  }, [openBlockerModal, goBackCallback, has_changed, step])});

  return null;
}

export function convertSectionsToRows(sections) {
  let rows = [];

  sections.forEach(section => {
    if (section.title) {
      rows.push({
        is_section: true,
        title: section.title,
        description: section.description
      });
    }

    section.criteria.forEach(criterion => {
      rows.push({
        is_section: false,
        title: criterion.title,
        description: criterion.description,
        descriptors: criterion.descriptors
      });
    });
  });

  return rows;
}

function convertRowsToSections(rows) {
  let sections = [];
  let currentSection = null;

  rows.forEach(row => {
    if (row.is_section) {
      // Start a new section
      currentSection = {
        title: row.title,
        description: row.description,
        criteria: []
      };
      sections.push(currentSection);
    } else {
      // Add criterion to current section or create a default section
      if (!currentSection) {
        currentSection = {
          title: null,
          description: null,
          criteria: []
        };
        sections.push(currentSection);
      }
      
      currentSection.criteria.push({
        title: row.title,
        description: row.description,
        descriptors: row.descriptors
      });
    }
  });

  return sections;
}